
import theme from '@/assets/theme.scss';
import { lightweightChartMixin } from '@/mixins';
import ChartService from '@/service/ChartService';
import FormatterService from "@/service/FormatterService";
import ShareService from '@/service/ShareService';

export default {
    name: 'PriceTargetConsensusChart',

    mixins: [lightweightChartMixin],

    props: {
        isin: {
            type: String,
            default: () => ''
        },
        interval: {
            type: String,
            default: () => ''
        }
    },

    data() {
        return {
            chart: null,
            datapoints: [],
            targetPrices: null,
            isLoading: false,
        }
    },

    watch: {
        isDarkMode: {
            immediate: true,
            handler(newState) {
                this.updateLineSeriesStyle(newState)
            }
        },
        isin() {
            this.fetchData();
        },
    },

    created() {
        this.format = new FormatterService();
        this.chartService = new ChartService(this.$axios, this.$store.state.preferences.isSandboxMode);
        this.shareService = new ShareService(this.$axios);

        this.fetchData();
    },

    methods: {
        async fetchChartData() {
            this.datapoints = [];
            try {
                const data = await this.chartService.get({
                source: 'share',
                sourceIdentifier: this.isin,
                interval: '2y',
            }, this.$t)

            this.datapoints = data.graphs[0].datapoints;
            } catch (e) {
                this.datapoints = [];
            }
            
        },
        async fetchSharePriceTargetConsensus() {
            this.targetPrices = null;
            try {
                const data = await this.shareService.getSharePriceTargetConsensus(this.isin)

                this.targetPrices = data;
            } catch (e) {
                this.targetPrices = null;
            }
        },
        async fetchData() {
            this.isLoading = true;
            
            await Promise.all([
                this.fetchChartData(),
                this.fetchSharePriceTargetConsensus()
            ]);
            this.isLoading = false;
            await this.$nextTick();

            this.drawChart();
        },

        drawChart() {
            // clear old chart data
            this.chart = null;
            this.areaSeries = null;
            this.currentPriceSeries = null;
            this.targetLowSeries = null;
            this.targetHighSeries = null;
            this.targetMedianSeries = null;

            if (!this.chart) {
                try {
                    this.createChart({ height: 300 });
                } catch (e) {
                    return;
                }
            }
            
            if (this.areaSeries) {
                this.chart.removeSeries(this.areaSeries);
            }

            this.areaSeries = this.chart.addAreaSeries(
                {
                    lineWidth: 2,
                    lineColor: this.isDarkMode ? theme['bg-brand-solid-dark'] : theme['bg-brand-solid-light'],
                    lineType: 2,
                    topColor: (this.isDarkMode ? theme['bg-brand-solid-dark'] : theme['bg-brand-solid-light']) + '55',
                    bottomColor: (this.isDarkMode ? theme['bg-brand-solid-dark'] : theme['bg-brand-solid-light']) + '00',
                    priceLineVisible: false,
                    lastValueVisible: false,
                }
            );
            this.areaSeries.setData((this.datapoints ?? []).map(datapoint => {
                return {
                    time: this.getTimezoneCorrectedTime(datapoint.t, true),
                    value: datapoint.c
                }
            }));
            
            if (this.datapoints.length > 0) {
                this.createCurrentPriceSeries();

                this.targetLowSeries = this.createTargetPriceSeries(this.targetLowSeries, this.targetPrices.targetLow, this.$t('component.share.priceTargetConsensusChart.min'));
                this.targetHighSeries = this.createTargetPriceSeries(this.targetHighSeries, this.targetPrices.targetHigh, this.$t('component.share.priceTargetConsensusChart.max'));
                this.targetMedianSeries = this.createTargetPriceSeries(this.targetMedianSeries, this.targetPrices.targetMedian, this.$t('component.share.priceTargetConsensusChart.avg'));
            }
        },
        updateLineSeriesStyle(isDarkMode) {
            this.lineSeries?.applyOptions({ color: isDarkMode ? theme['bg-brand-solid-dark'] : theme['bg-brand-solid-light'] })
        },
        createTargetPriceSeries(series, value, title) {
            if (series) {
                this.chart.removeSeries(series);
            }
            const forecastTime = new Date(this.datapoints[this.datapoints.length - 1].t);
            forecastTime.setFullYear(forecastTime.getFullYear() + 1);
            const percentChange = ((value - this.datapoints[this.datapoints.length - 1].c) / this.datapoints[this.datapoints.length - 1].c) * 100;
            const percentChangeFormatted = (percentChange >= 0 ? '+' : '') + this.format.percent(percentChange);
            const newSeries =  this.chart.addLineSeries(
                {
                    lineWidth: 2,
                    color: this.isDarkMode
                        ? (percentChange >= 0 ? theme['chart-positive-dark'] : theme['chart-negative-dark'])
                        : (percentChange >= 0 ? theme['chart-positive-light'] : theme['chart-negative-light']),
                    lineStyle: 2,
                    priceLineVisible: false,
                    title: `${title} ${percentChangeFormatted}`
                }
            );
            newSeries.setData([
                {
                    time: this.getTimezoneCorrectedTime(this.datapoints[this.datapoints.length - 1].t, true),
                    value: this.datapoints[this.datapoints.length - 1].c
                },
                {
                    time: this.getTimezoneCorrectedTime(forecastTime, true),
                    value: value,
                },
            ]);

            return newSeries;
        },
        createCurrentPriceSeries() {
            if (!this.currentPriceSeries) {
                this.currentPriceSeries = this.chart.addLineSeries(
                    {
                        lineWidth: 2,
                        color: this.isDarkMode ? theme['bg-brand-solid-dark'] : theme['bg-brand-solid-light'],
                        priceLineVisible: false,
                        title: this.$t('component.share.priceTargetConsensusChart.current'),
                    }
                );
            }
            const forecastTime = new Date(this.datapoints[this.datapoints.length - 1].t);
            forecastTime.setFullYear(forecastTime.getFullYear() + 1);
            const lastTime = new Date(this.datapoints[this.datapoints.length - 1].t);

            // add whitespace points to scale time axis
            const numberOfWhitespacePoints = Math.round(this.datapoints.length / 3);
            const whitespaceData = Array.from(Array(numberOfWhitespacePoints).keys()).map(i => {
                const newDate = new Date(forecastTime.getTime() - (i * (forecastTime.getTime() - lastTime.getTime()) / numberOfWhitespacePoints));
                newDate.setSeconds(newDate.getSeconds() - (this.datapoints.length - i));

                return {
                    time: this.getTimezoneCorrectedTime(newDate, true),
                }
            });
            
            this.currentPriceSeries.setData([
                {
                    time: this.getTimezoneCorrectedTime(this.datapoints[this.datapoints.length - 1].t, true),
                    value: this.datapoints[this.datapoints.length - 1].c
                },
                ...whitespaceData,
                {
                    time: this.getTimezoneCorrectedTime(forecastTime, true),
                    value: this.datapoints[this.datapoints.length - 1].c,
                },
            ]);
        }
    },
}
